<template>
  <div class="embedded-view p-4">
    <div v-if="loading" class="embedded-loading">
      <span>Carregando...</span>
    </div>

    <template v-else>
      <vs-alert icon="warning" active="true" color="warning" class="my-5" v-if="showEmptyDataMessage">
        <span>Acesso negado ao módulo.</span>
      </vs-alert>

      <vs-alert icon="warning" active="true" color="warning" class="my-5" v-else-if="showNoFieldsMessage">
        <span>Este módulo não possui campos.</span>
      </vs-alert>

      <module-form
        v-else
        :delegatorUrl="detail.api_delegator"
        :detailProp="detail"
        :blockDataProp="blockData"
        :permissionsProp="permissions"
        :enableEditProp="enableEdit"
        action-type="view"
        :blockTabs="blockTabs"
        :relatedReadOnly="true"
      />
    </template>
  </div>
</template>

<script>
import ModuleForm from '../../../components/custom-modules/CustomModuleForm.vue'
import { setApplicationLocalStorageItem, performFetch } from '../../../helpers'

export default {
  components: {
    ModuleForm
  },
  data() {
    return {
      loading: true,
      detail: {},
      blockData: [],
      blockTabs: {},
      permissions: { can_edit: false },
      enableEdit: {},
      fieldModel: {},
      optionFieldModel: [],
      fieldTitle: {},
      fieldName: {},
      fieldType: {},
      showEmptyDataMessage: false,
      showNoFieldsMessage: false
    }
  },
  async mounted() {
    await this.getData()
  },
  watch: {
    async '$route.params.moduleHash'() {
      this.loading = true
      this.showEmptyDataMessage = false
      this.showNoFieldsMessage = false
      this.blockData = []
      this.blockTabs = {}
      this.detail = {}
      await this.getData()
    }
  },
  methods: {
    setFieldsModels(fieldItem) {
      if (fieldItem.payload === '') {
        fieldItem.payload = null
      }

      if (fieldItem.type_id === 20 || fieldItem.type_id === 16 || fieldItem.type_id === 18) {
        fieldItem.payload = (fieldItem.payload === '' || fieldItem.payload === null) ? 0 : parseFloat(fieldItem.payload)
      } else if (fieldItem.type_id === 24) {
        this.$set(this.fieldModel, fieldItem.hash, {})
        fieldItem.options.forEach((opt) => {
          if (opt.selected) {
            this.fieldModel[fieldItem.hash] = { hash: opt.value, name: opt.label }
          }
        })
      } else if (fieldItem.type_id === 29) {
        this.$set(this.fieldModel, fieldItem.hash, {})
        const optionsList = fieldItem.options.map(opt => ({ hash: opt.value, label: opt.label, color: opt.color }))
        const defaultValue = optionsList.find(x => x.label === fieldItem.default_value)
        this.$set(this.fieldModel, fieldItem.hash, defaultValue)
      } else if (fieldItem.type_id === 25 || fieldItem.type_id === 30) {
        this.$set(this.fieldModel, fieldItem.hash, {})
        if (Array.isArray(fieldItem.options)) {
          fieldItem.options.forEach((opt) => {
            if (opt.selected) {
              this.fieldModel[fieldItem.hash] = { hash: opt.value, name: opt.label }
            }
          })
        }
      }

      if (![24, 25, 29, 30].includes(fieldItem.type_id)) {
        this.$set(this.fieldModel, fieldItem.hash, fieldItem.payload)
      }

      this.$set(this.fieldTitle, fieldItem.hash, fieldItem.title)
      this.$set(this.fieldName, fieldItem.hash, fieldItem.name)
      this.$set(this.fieldType, fieldItem.hash, fieldItem.type_id)
    },
    buildBlocks() {
      for (const block of this.blockData) {
        for (const fieldItem of block.fields) {
          this.setFieldsModels(fieldItem)
        }
      }
    },
    buildBlockTabs(responseData) {
      const blockTabs = {}

      if (responseData.custom_module_tabs && Object.keys(responseData.custom_module_tabs).length > 0) {
        Object.entries(responseData.custom_module_tabs).forEach((objectData) => {
          const [blockHash, tabs] = objectData

          if (!blockTabs.hasOwnProperty(blockHash)) {
            blockTabs[blockHash] = []
          }

          tabs.forEach((value, key) => {
            const icon = 'icon' + value.icon.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`).replace('-icon', '')

            blockTabs[blockHash].push({
              tabIndex: key,
              index: value.index,
              name: value.module_name,
              icon: icon,
              tabName: value.tab_name === null ? value.module_name : value.tab_name,
              tabName_en: value.tab_name_en || null,
              recordHash: value.record_hash,
              moduleHash: value.module_hash,
              fieldHash: value.field_hash,
              callApi: value.call_api
            })
          })
        })
      }

      this.blockTabs = blockTabs
    },
    async getData() {
      let pendingPayload = null
      let pendingCanEdit = false

      try {
        const requestBody = {
          application_token: this.$store.state.auth.app_token,
          module_token: this.$route.params.moduleHash,
          session_token: this.$store.state.auth.token
        }

        if (this.$route.params.rowHash) {
          requestBody['row_hash'] = this.$route.params.rowHash
        }

        const response = await performFetch(this.$store.state.auth.api_url, {
          command: 'get_module_row',
          parameters: requestBody
        })

        if (response.status === 200) {
          const responseData = await response.json()

          this.buildBlockTabs(responseData)

          this.detail = responseData.detail
          this.detail.module_hash = this.$route.params.moduleHash
          document.title = `${document.title.split(' | ')[0]} | ${this.$displayLabel(responseData.detail, 'module_name')}`
          this.$store.commit('auth/SET_CURRENT_MODULE_NAME', {
            module_name: responseData.detail.module_name,
            module_name_en: responseData.detail.module_name_en
          })

          if (responseData.payload.length > 0) {
            // Save payload to be set AFTER loading=false so the blockDataProp
            // watcher in CustomModuleForm fires (watcher is not immediate).
            pendingPayload = responseData.payload
            pendingCanEdit = responseData.permissions.can_edit
            const visibleBlocks = responseData.payload.filter((el) => el.visible)
            if (visibleBlocks.length === 0) {
              this.showNoFieldsMessage = true
            }
          } else {
            this.showEmptyDataMessage = true
          }

        } else if (response.status === 401) {
          setApplicationLocalStorageItem('lastUrl', btoa(window.location.pathname))
          this.$store.dispatch('auth/signOut')
        } else {
          this.showEmptyDataMessage = true
        }
      } catch (e) {
        this.showEmptyDataMessage = true
      } finally {
        // Set loading=false first so module-form mounts with blockData=[].
        // The $nextTick below then sets blockData, triggering the watcher inside CustomModuleForm.
        this.loading = false
      }

      if (pendingPayload) {
        await this.$nextTick()
        this.blockData = pendingPayload
        this.buildBlocks()
        this.$set(this.permissions, 'can_edit', pendingCanEdit)
      }
    }
  }
}
</script>

<style scoped>
.embedded-view {
  min-height: 100vh;
  background: #fff;
}

.embedded-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #636b6f;
  font-size: 14px;
}
</style>
